import React from 'react';
import type { CensusSummary } from '../../types/calculations';
import { Users, Home, Briefcase, DollarSign, GraduationCap, Building2 } from 'lucide-react';

interface Props {
  census: CensusSummary;
  marketName?: string;
}

/** Format a number with commas (e.g. 52340 → "52,340") */
const fmtNum = (n: number | null | undefined): string =>
  n != null ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '--';

/** Format a dollar amount (e.g. 52340 → "$52,340") */
const fmtDollar = (n: number | null | undefined): string =>
  n != null ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '--';

/** Format a percentage (e.g. 42.3 → "42.3%") */
const fmtPct = (n: number | null | undefined): string =>
  n != null ? `${n.toFixed(1)}%` : '--';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sublabel?: string;
  accent?: 'gold' | 'emerald' | 'purple' | 'amber' | 'default';
}

const accentColors: Record<string, string> = {
  gold: 'text-gold',
  emerald: 'text-emerald-400',
  purple: 'text-purple-300',
  amber: 'text-amber-400',
  default: 'text-ivory',
};

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sublabel, accent = 'default' }) => (
  <div className="p-3 bg-graphite rounded border border-slate-dark">
    <div className="flex items-center gap-1.5 mb-1">
      <span className="text-stone-700">{icon}</span>
      <p className="text-sm text-stone-700 uppercase tracking-wider">{label}</p>
    </div>
    <p className={`text-sm font-semibold font-mono ${accentColors[accent]}`}>{value}</p>
    {sublabel && <p className="text-sm text-stone-700 mt-0.5">{sublabel}</p>}
  </div>
);

/** Bar chart row for breakdown data */
const BreakdownBar: React.FC<{ label: string; value: number; maxValue: number }> = ({ label, value, maxValue }) => {
  const width = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-stone-700 w-32 truncate" title={label}>{label}</span>
      <div className="flex-1 bg-charcoal/50 rounded-full h-2 overflow-hidden">
        <div className="h-full bg-gold/60 rounded-full" style={{ width: `${width}%` }} />
      </div>
      <span className="font-mono text-stone-700 w-12 text-right">{value.toFixed(1)}%</span>
    </div>
  );
};

/**
 * Census Demographic Panel — displays raw Census ACS tract-level data
 * for properties in any market. Primary intelligence source for non-Philly
 * markets where Zandi scoring is unavailable.
 */
export const CensusDemographicPanel: React.FC<Props> = ({ census, marketName }) => {
  if (!census?.found) return null;

  // Find max value for bar scaling
  const industryEntries = census.industry_breakdown
    ? Object.entries(census.industry_breakdown)
        .filter(([, v]) => v != null && v > 0)
        .sort(([, a], [, b]) => b - a)
    : [];
  const maxIndustry = industryEntries.length > 0 ? industryEntries[0][1] : 1;

  const formatIndustryLabel = (key: string): string =>
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-gold" />
          <h3 className="text-lg font-serif italic text-gold">
            {marketName ? `${marketName} Census Profile` : 'Census Demographic Profile'}
          </h3>
        </div>
        {census.tract_name && (
          <span className="text-sm text-stone-700 italic">{census.tract_name}</span>
        )}
      </div>

      {/* Primary stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<DollarSign className="w-3.5 h-3.5" />}
          label="Median Income"
          value={fmtDollar(census.median_household_income)}
          sublabel={census.monthly_income ? `${fmtDollar(census.monthly_income)}/mo` : undefined}
          accent="gold"
        />
        <StatCard
          icon={<Home className="w-3.5 h-3.5" />}
          label="Median Rent"
          value={fmtDollar(census.median_gross_rent)}
          sublabel={census.implied_rent_to_income != null ? `${census.implied_rent_to_income.toFixed(0)}% of income` : undefined}
          accent="purple"
        />
        <StatCard
          icon={<Users className="w-3.5 h-3.5" />}
          label="Renter Households"
          value={fmtPct(census.pct_renters)}
          sublabel={census.renter_households ? `${fmtNum(census.renter_households)} units` : undefined}
          accent="emerald"
        />
        <StatCard
          icon={<Briefcase className="w-3.5 h-3.5" />}
          label="Unemployment"
          value={fmtPct(census.unemployment_rate)}
          sublabel={census.labor_force ? `${fmtNum(census.labor_force)} labor force` : undefined}
          accent={census.unemployment_rate != null && census.unemployment_rate > 8 ? 'amber' : 'default'}
        />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          icon={<GraduationCap className="w-3.5 h-3.5" />}
          label="College Educated"
          value={fmtPct(census.pct_bachelors_plus)}
        />
        <StatCard
          icon={<Users className="w-3.5 h-3.5" />}
          label="Median Age"
          value={census.median_age != null ? census.median_age.toFixed(1) : '--'}
        />
        <StatCard
          icon={<Home className="w-3.5 h-3.5" />}
          label="Rent Burdened"
          value={fmtPct(census.pct_rent_burdened)}
          sublabel={census.pct_severely_burdened != null ? `${census.pct_severely_burdened.toFixed(0)}% severe` : undefined}
          accent={census.pct_rent_burdened != null && census.pct_rent_burdened > 50 ? 'amber' : 'default'}
        />
        <StatCard
          icon={<Building2 className="w-3.5 h-3.5" />}
          label="Housing Units"
          value={fmtNum(census.housing_units)}
        />
      </div>

      {/* Employment stability indicators */}
      {(census.pct_stable_industries != null || census.pct_professional != null) && (
        <div className="p-4 bg-graphite rounded-lg border border-slate-dark">
          <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">
            Employment Stability
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {census.pct_professional != null && (
              <div>
                <p className="text-sm text-stone-700">Professional/Mgmt</p>
                <p className="text-sm font-mono text-ivory">{fmtPct(census.pct_professional)}</p>
              </div>
            )}
            {census.pct_stable_industries != null && (
              <div>
                <p className="text-sm text-stone-700">Stable Industries</p>
                <p className="text-sm font-mono text-emerald-400">{fmtPct(census.pct_stable_industries)}</p>
              </div>
            )}
            {census.pct_cyclical_industries != null && (
              <div>
                <p className="text-sm text-stone-700">Cyclical Industries</p>
                <p className="text-sm font-mono text-amber-400">{fmtPct(census.pct_cyclical_industries)}</p>
              </div>
            )}
            {census.pct_stable_employers != null && (
              <div>
                <p className="text-sm text-stone-700">Govt + Nonprofit</p>
                <p className="text-sm font-mono text-ivory">{fmtPct(census.pct_stable_employers)}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Industry breakdown bars */}
      {industryEntries.length > 0 && (
        <div className="p-4 bg-graphite rounded-lg border border-slate-dark">
          <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">
            Industry Breakdown
          </h4>
          <div className="space-y-1.5">
            {industryEntries.slice(0, 8).map(([key, value]) => (
              <BreakdownBar key={key} label={formatIndustryLabel(key)} value={value} maxValue={maxIndustry} />
            ))}
          </div>
        </div>
      )}

      {/* Commute & Transport */}
      {(census.commute_breakdown || census.transport_breakdown) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {census.commute_breakdown && Object.keys(census.commute_breakdown).length > 0 && (
            <div className="p-4 bg-graphite rounded-lg border border-slate-dark">
              <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">
                Commute Time
              </h4>
              <div className="space-y-1.5">
                {Object.entries(census.commute_breakdown).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-stone-700">{key.replace(/_/g, ' ').replace('short ', '< ').replace('medium ', '').replace('long ', '')}</span>
                    <span className="font-mono text-stone-300">{value.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {census.transport_breakdown && Object.keys(census.transport_breakdown).length > 0 && (
            <div className="p-4 bg-graphite rounded-lg border border-slate-dark">
              <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">
                Transportation Mode
              </h4>
              <div className="space-y-1.5">
                {Object.entries(census.transport_breakdown)
                  .filter(([, v]) => v > 0)
                  .sort(([, a], [, b]) => b - a)
                  .map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-stone-700">{key.replace(/_/g, ' ')}</span>
                      <span className="font-mono text-stone-300">{value.toFixed(1)}%</span>
                    </div>
                  ))}
              </div>
              {census.pct_urban_commute != null && (
                <div className="mt-2 pt-2 border-t border-slate-dark flex justify-between text-sm">
                  <span className="text-stone-700 font-semibold">Urban commute</span>
                  <span className="font-mono text-purple-300">{census.pct_urban_commute.toFixed(1)}%</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Data source footer */}
      <p className="text-sm text-stone-700 italic text-right">
        Source: U.S. Census Bureau ACS 5-Year Estimates
      </p>
    </div>
  );
};
